/**
 * GoodGuardian — Parenting Time Calculator
 *
 * Tracks actual vs. court-ordered custody time percentages using
 * manually logged custody periods. Exchange events from the event
 * log are surfaced as date shortcuts. Data is server-backed via
 * /api/data/parenting-time.
 */
import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import { useApp } from '@/context/AppContext'
import { todayYMD } from '@/utils/dateUtils'
import { data as dataApi } from '@/utils/auth'

// ── Date math ──────────────────────────────────────────────────────────────────
/** Inclusive day count: Jan 1 → Jan 3 = 3 days */
function daysBetween(start, end) {
  const a = new Date(start + 'T00:00:00')
  const b = new Date(end   + 'T00:00:00')
  return Math.max(0, Math.round((b - a) / 86400000) + 1)
}

/** Days of [period.start, period.end] that fall inside [from, to] */
function overlapDays(period, from, to) {
  const s = period.start > from ? period.start : from
  const e = period.end   < to   ? period.end   : to
  if (s > e) return 0
  return daysBetween(s, e)
}

function monthsAgoStr(n) {
  const d = new Date()
  d.setMonth(d.getMonth() - n)
  return d.toISOString().split('T')[0]
}

function getMonthsInRange(from, to) {
  const months = []
  const d = new Date(from + 'T00:00:00')
  d.setDate(1)
  const end = new Date(to + 'T00:00:00')
  while (d <= end) {
    const y  = d.getFullYear()
    const m  = String(d.getMonth() + 1).padStart(2, '0')
    const last = new Date(y, d.getMonth() + 1, 0)
    months.push({
      label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      from:  `${y}-${m}-01`,
      to:    `${y}-${m}-${String(last.getDate()).padStart(2, '0')}`,
    })
    d.setMonth(d.getMonth() + 1)
  }
  return months
}

function fmtDate(ymd) {
  if (!ymd) return ''
  const [y, m, d] = ymd.split('-')
  return `${m}/${d}/${y}`
}

// ── Constants ──────────────────────────────────────────────────────────────────
const PRESETS = [
  { label: '50/50', value: 50 },
  { label: '60/40', value: 60 },
  { label: '65/35', value: 65 },
  { label: '70/30', value: 70 },
  { label: '80/20', value: 80 },
]

const QUICK_RANGES = [
  { label: '3 mo',  fn: () => monthsAgoStr(3)  },
  { label: '6 mo',  fn: () => monthsAgoStr(6)  },
  { label: '1 yr',  fn: () => monthsAgoStr(12) },
  { label: 'All',   fn: null                   },
]

// ── Screen ─────────────────────────────────────────────────────────────────────
export default function ParentingTime() {
  const { events } = useApp()

  const [data,      setData]      = useState({ orderedPct: 50, periods: [] })
  const [loading,   setLoading]   = useState(true)
  const [rangeFrom, setRangeFrom] = useState(monthsAgoStr(6))
  const [rangeTo,   setRangeTo]   = useState(todayYMD())
  const [showForm,    setShowForm]    = useState(false)
  const [addForm,     setAddForm]     = useState({ start: todayYMD(), end: todayYMD(), note: '' })
  const [showHints,   setShowHints]   = useState(false)
  const [copied,      setCopied]      = useState(false)
  const [periodError, setPeriodError] = useState('')

  // ── Load from server on mount ──────────────────────────────────────────────
  useEffect(() => {
    setLoading(true)
    dataApi.getParentingTime()
      .then(res => {
        setData({
          orderedPct: res.ordered_pct ?? 50,
          periods: (res.periods || []).map(p => ({
            id:    p.id,
            start: p.start_date,
            end:   p.end_date,
            note:  p.note || '',
          })),
        })
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // ── Debounced ordered_pct sync ─────────────────────────────────────────────
  const pctTimerRef = useRef(null)

  const updateOrderedPct = useCallback((value) => {
    setData(prev => ({ ...prev, orderedPct: value }))
    if (pctTimerRef.current) clearTimeout(pctTimerRef.current)
    pctTimerRef.current = setTimeout(() => {
      dataApi.addParentingPeriod({ ordered_pct: value }).catch(() => {})
    }, 800)
  }, [])

  // ── Custody exchange events as date hints ──────────────────────────────────
  const exchangeEvents = useMemo(() =>
    events
      .filter(e => e.type === 'exchange')
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 30),
    [events]
  )

  // ── Add / delete periods ───────────────────────────────────────────────────
  async function addPeriod() {
    if (!addForm.start || !addForm.end) return
    if (addForm.start > addForm.end) {
      setPeriodError('End date must be on or after the start date')
      return
    }
    setPeriodError('')
    try {
      const res = await dataApi.addParentingPeriod({
        start_date: addForm.start,
        end_date:   addForm.end,
        note:       addForm.note.trim() || undefined,
      })
      if (res.period) {
        const newPeriod = {
          id:    res.period.id,
          start: res.period.start_date,
          end:   res.period.end_date,
          note:  res.period.note || '',
        }
        setData(prev => ({
          ...prev,
          periods: [...prev.periods, newPeriod].sort((a, b) => a.start.localeCompare(b.start)),
        }))
      }
    } catch {}
    setAddForm({ start: todayYMD(), end: todayYMD(), note: '' })
    setShowForm(false)
    setShowHints(false)
  }

  function deletePeriod(id) {
    setData(prev => ({ ...prev, periods: prev.periods.filter(p => p.id !== id) }))
    dataApi.deleteParentingPeriod(id).catch(() => {})
  }

  // ── Statistics ─────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    // Resolve effective range
    let from = rangeFrom
    let to   = rangeTo || todayYMD()
    if (!from) {
      // "All time" — use earliest period start
      from = data.periods.length > 0
        ? data.periods.reduce((min, p) => p.start < min ? p.start : min, data.periods[0].start)
        : monthsAgoStr(6)
    }
    if (from > to) return null

    const totalDays   = daysBetween(from, to)
    const orderedDays = Math.round(totalDays * data.orderedPct / 100)
    const actualDays  = data.periods.reduce((sum, p) => sum + overlapDays(p, from, to), 0)
    const actualPct   = totalDays > 0 ? Math.round(actualDays / totalDays * 100) : 0
    const deficitDays = orderedDays - actualDays   // positive = deficit, negative = surplus

    const months = getMonthsInRange(from, to).map(m => {
      const mTotal   = daysBetween(m.from, m.to)
      const mOrdered = Math.round(mTotal * data.orderedPct / 100)
      const mActual  = data.periods.reduce((sum, p) => sum + overlapDays(p, m.from, m.to), 0)
      return { ...m, total: mTotal, ordered: mOrdered, actual: mActual, pct: mTotal > 0 ? Math.round(mActual / mTotal * 100) : 0 }
    })

    return { from, to, totalDays, orderedDays, actualDays, actualPct, deficitDays, months }
  }, [data.orderedPct, data.periods, rangeFrom, rangeTo])

  const onTrack = stats ? stats.deficitDays <= 0 : false

  const filingText = stats && !onTrack && stats.deficitDays > 7
    ? `In the period from ${fmtDate(stats.from)} to ${fmtDate(stats.to)}, I received ${stats.actualDays} days (${stats.actualPct}%) of parenting time against a court-ordered ${data.orderedPct}% (${stats.orderedDays} days) — a deficit of ${stats.deficitDays} days.`
    : null

  function copyFilingText() {
    if (!filingText) return
    navigator.clipboard?.writeText(filingText).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const addDays = addForm.start && addForm.end && addForm.start <= addForm.end
    ? daysBetween(addForm.start, addForm.end)
    : null

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900">
      <AppHeader title="Parenting Time" showBack />

      <div className="screen no-scrollbar px-4 py-5 space-y-4">

        {/* ── Loading spinner ── */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="w-8 h-8 rounded-full border-4 border-brand-100 border-t-brand-700 animate-spin" />
          </div>
        )}

        {!loading && <>

        {/* ── Court-Ordered % ── */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1">Court-Ordered Time (My Share)</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">The percentage of time you are court-ordered to have the child(ren).</p>

          <div className="flex flex-wrap gap-2 mb-3">
            {PRESETS.map(p => (
              <button key={p.value}
                onClick={() => updateOrderedPct(p.value)}
                className={`text-xs px-3 py-1.5 rounded-full font-semibold border-2 transition-all ${
                  data.orderedPct === p.value
                    ? 'bg-brand-700 text-white border-brand-700'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'
                }`}>
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <input type="range" min={1} max={99} value={data.orderedPct}
              onChange={e => updateOrderedPct(Number(e.target.value))}
              className="flex-1 accent-brand-600" />
            <div className="flex items-center gap-1 flex-shrink-0">
              <input type="number" min={1} max={99} value={data.orderedPct}
                onChange={e => {
                  const v = Math.min(99, Math.max(1, Number(e.target.value)))
                  if (!isNaN(v)) updateOrderedPct(v)
                }}
                className="w-14 border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1.5 text-sm text-center font-bold text-brand-700 bg-white dark:bg-slate-800" />
              <span className="text-sm text-slate-500">%</span>
            </div>
          </div>
        </div>

        {/* ── Report Period ── */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Report Period</h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">From</label>
              <input type="date" value={rangeFrom} onChange={e => setRangeFrom(e.target.value)}
                className="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100" />
            </div>
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">To</label>
              <input type="date" value={rangeTo} max={todayYMD()} onChange={e => setRangeTo(e.target.value)}
                className="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {QUICK_RANGES.map(opt => (
              <button key={opt.label}
                onClick={() => { setRangeTo(todayYMD()); setRangeFrom(opt.fn ? opt.fn() : '') }}
                className="text-xs px-3 py-1.5 rounded-full bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/40 font-medium transition-colors">
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Report ── */}
        {stats && data.periods.length > 0 && (
          <div className="card p-4 space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Summary</h3>

            {/* Big three numbers */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl py-3">
                <p className="text-2xl font-extrabold text-brand-700">{data.orderedPct}%</p>
                <p className="text-xs text-slate-500 mt-0.5">Ordered</p>
                <p className="text-xs text-slate-400">{stats.orderedDays}d</p>
              </div>
              <div className={`rounded-xl py-3 ${onTrack ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                <p className={`text-2xl font-extrabold ${onTrack ? 'text-green-600' : 'text-red-600'}`}>{stats.actualPct}%</p>
                <p className="text-xs text-slate-500 mt-0.5">Actual</p>
                <p className="text-xs text-slate-400">{stats.actualDays}d</p>
              </div>
              <div className={`rounded-xl py-3 ${onTrack ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                <p className={`text-2xl font-extrabold ${onTrack ? 'text-green-600' : 'text-red-600'}`}>
                  {onTrack ? '+' : '-'}{Math.abs(stats.deficitDays)}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{onTrack ? 'Surplus' : 'Deficit'}</p>
                <p className="text-xs text-slate-400">days</p>
              </div>
            </div>

            {/* Progress bar */}
            <div>
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>0%</span>
                <span className="font-medium text-brand-700">{data.orderedPct}% ordered</span>
                <span>100%</span>
              </div>
              <div className="relative h-5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                {/* Ordered target line */}
                <div className="absolute top-0 bottom-0 w-0.5 bg-brand-700 z-10"
                  style={{ left: `${data.orderedPct}%` }} />
                {/* Actual fill */}
                <div className={`h-full rounded-full transition-all ${onTrack ? 'bg-green-400' : 'bg-red-400'}`}
                  style={{ width: `${Math.min(stats.actualPct, 100)}%` }} />
              </div>
              <p className={`text-xs mt-1.5 font-medium ${onTrack ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {onTrack
                  ? `On track — ${Math.abs(stats.deficitDays)} day${Math.abs(stats.deficitDays) !== 1 ? 's' : ''} above your court order`
                  : `Behind by ${stats.deficitDays} day${stats.deficitDays !== 1 ? 's' : ''} — ${stats.actualPct}% actual vs. ${data.orderedPct}% ordered`}
              </p>
            </div>

            {/* Monthly breakdown */}
            {stats.months.length > 1 && (
              <div>
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Monthly Breakdown</p>
                <div className="space-y-2.5">
                  {stats.months.map(m => {
                    const mOk  = m.actual >= m.ordered
                    const barW = m.total > 0 ? Math.min(100, Math.round(m.actual / m.total * 100)) : 0
                    return (
                      <div key={m.label}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-slate-500 dark:text-slate-400 w-20 flex-shrink-0">{m.label}</span>
                          <span className={`text-xs font-semibold ml-auto ${mOk ? 'text-green-600' : 'text-red-500'}`}>
                            {m.actual}d / {m.ordered}d ({m.pct}%)
                          </span>
                        </div>
                        <div className="relative h-2.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div className="absolute top-0 bottom-0 w-px bg-brand-400 z-10"
                            style={{ left: `${data.orderedPct}%` }} />
                          <div className={`h-full rounded-full transition-all ${mOk ? 'bg-green-400' : 'bg-red-300'}`}
                            style={{ width: `${barW}%` }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Modification filing callout */}
            {filingText && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">For modification filing:</p>
                  <button onClick={copyFilingText}
                    className="text-xs font-semibold text-brand-700 dark:text-brand-300 hover:text-brand-900 transition-colors">
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed italic">"{filingText}"</p>
              </div>
            )}
          </div>
        )}

        {/* Empty report prompt */}
        {data.periods.length === 0 && (
          <div className="card p-5 text-center bg-slate-50 dark:bg-slate-800">
            <span className="text-3xl block mb-2">📅</span>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">No custody periods logged yet</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Add each consecutive stretch of days you had the child(ren) below.
            </p>
          </div>
        )}

        {/* ── Custody Periods ── */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-1">
            <div>
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">My Custody Periods</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Each period = consecutive days you had the child(ren).</p>
            </div>
            <button
              onClick={() => { setShowForm(p => !p); setShowHints(false) }}
              className="tap-target w-8 h-8 rounded-full bg-brand-50 dark:bg-brand-900/20 flex items-center justify-center text-brand-700 hover:bg-brand-100 transition-colors"
              aria-label="Add custody period">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          {/* Add form */}
          {showForm && (
            <div className="mt-3 bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-700 rounded-xl p-3 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Start date</label>
                  <input type="date" value={addForm.start}
                    onChange={e => { setPeriodError(''); setAddForm(p => ({ ...p, start: e.target.value })) }}
                    className="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">End date</label>
                  <input type="date" value={addForm.end} min={addForm.start}
                    onChange={e => { setPeriodError(''); setAddForm(p => ({ ...p, end: e.target.value })) }}
                    className="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100" />
                </div>
              </div>

              {periodError && <p className="text-xs text-red-600 font-medium">{periodError}</p>}

              {addDays !== null && (
                <p className="text-xs font-semibold text-brand-700 dark:text-brand-300">
                  {addDays} day{addDays !== 1 ? 's' : ''}
                </p>
              )}

              <input type="text" value={addForm.note} placeholder="Note (optional)"
                onChange={e => setAddForm(p => ({ ...p, note: e.target.value }))}
                className="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder:text-slate-400" />

              {/* Exchange event date hints */}
              {exchangeEvents.length > 0 && (
                <button type="button" onClick={() => setShowHints(p => !p)}
                  className="text-xs text-brand-600 dark:text-brand-400 font-medium hover:text-brand-800">
                  {showHints ? '▲ Hide' : '▼ Fill from exchange events'}
                </button>
              )}

              {showHints && (
                <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600 p-2 max-h-44 overflow-y-auto no-scrollbar">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Tap to set start or end date:</p>
                  <div className="space-y-1">
                    {exchangeEvents.map(e => (
                      <div key={e.id} className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 w-24 flex-shrink-0">{e.date}</span>
                        <button type="button"
                          onClick={() => setAddForm(p => ({ ...p, start: e.date }))}
                          className="text-xs px-2 py-1 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 rounded font-medium hover:bg-brand-100 transition-colors">
                          Set start
                        </button>
                        <button type="button"
                          onClick={() => setAddForm(p => ({ ...p, end: e.date }))}
                          className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded font-medium hover:bg-slate-200 transition-colors">
                          Set end
                        </button>
                        <span className="text-xs text-slate-400 truncate min-w-0">{e.description?.slice(0, 30)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="ghost" size="sm" className="flex-1"
                  onClick={() => { setShowForm(false); setShowHints(false) }}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" className="flex-1"
                  disabled={!addForm.start || !addForm.end || addForm.start > addForm.end}
                  onClick={addPeriod}>
                  Add Period
                </Button>
              </div>
            </div>
          )}

          {/* Periods list */}
          {data.periods.length === 0 && !showForm && (
            <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-4">
              No periods yet — tap + to add your first.
            </p>
          )}

          {data.periods.length > 0 && (
            <div className="mt-3 space-y-0 divide-y divide-slate-100 dark:divide-slate-700">
              {[...data.periods]
                .sort((a, b) => b.start.localeCompare(a.start))
                .map(p => {
                  const days = daysBetween(p.start, p.end)
                  return (
                    <div key={p.id} className="flex items-center justify-between py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                          {fmtDate(p.start)} → {fmtDate(p.end)}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {days} day{days !== 1 ? 's' : ''}{p.note ? ` · ${p.note}` : ''}
                        </p>
                      </div>
                      <button onClick={() => deletePeriod(p.id)}
                        className="tap-target text-slate-300 dark:text-slate-600 hover:text-red-500 transition-colors flex-shrink-0 ml-3"
                        aria-label="Delete period">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )
                })}
            </div>
          )}

          {data.periods.length > 0 && (
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-2 text-center">
              {data.periods.length} period{data.periods.length !== 1 ? 's' : ''} ·{' '}
              {data.periods.reduce((s, p) => s + daysBetween(p.start, p.end), 0)} total days logged
            </p>
          )}
        </div>

        {/* Disclaimer */}
        <div className="disclaimer">
          <p className="text-xs leading-relaxed">
            <strong>⚠️ Attorney review required.</strong> This calculator uses dates you log manually. Verify all totals with your attorney before including in any court filing or modification petition.
          </p>
        </div>

        <div className="pb-4" />

        </>}
      </div>
    </div>
  )
}
